import { createAsyncThunk } from "@reduxjs/toolkit";
import apiConfig from "constants/apiConfig";
import {
    fetchBasicSocialPost,
    fetchSocialAccount,
    fetchSocialAccountApplications,
    fetchSocialAccountCollaborations,
    fetchSocialPost,
    fetchSocialPosts,
} from "services/api/influencer";
import { ERROR } from "types";
import { BasicSocialPost, SocialPost } from "types/collaboration";
import { influencerSlice, SocialAccount } from "types/influencer";
import {
    SocialAccountApplicationItem,
    SocialAccountCollaborationItem,
} from "types/social-account";
import { getErrorMessage, isFresh } from "utils/helpers";

export type SocialMediaPostType = 'REELS' | 'FEED' | 'VIDEO';

export const getSocialMediaCacheKey = (
    type: SocialMediaPostType,
    params?: { sort?: string; has_brand_collabs?: 'y' }
) => {
    if (params?.has_brand_collabs === 'y') {
        return `${type}_brand_collabs`;
    }
    if (params?.sort && params.sort !== 'like_count') {
        return `${type}_${params.sort}`;
    }
    return type;
};

const normalizeList = <T,>(data: { items?: T[] } | T[] | null | undefined): T[] => {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.items)) return data.items;
    return [];
};

export const fetchInfluencerSocialAccount = createAsyncThunk<
    { socialAccountId: string; socialAccount: SocialAccount },
    { socialAccountId: string; force?: boolean },
    { rejectValue: string }
>(
    'influencer/social-account',
    async ({ socialAccountId, force = false }, { getState, rejectWithValue }) => {
        try {
            const state = getState() as { influencers: influencerSlice };
            const STALE_TIME = apiConfig.FETCH_SOCIAL_ACCOUNT.staleTime;

            if (!force && isFresh(state.influencers.social_accounts?.[socialAccountId]?.last_fetched_at ?? null, STALE_TIME)) {
                return rejectWithValue(ERROR.CACHED);
            }

            const { social_account } = await fetchSocialAccount(socialAccountId);
            return { socialAccountId, socialAccount: social_account };
        } catch (error) {
            return rejectWithValue(getErrorMessage(error, 'failed to fetch social account'));
        }
    }
);

export const fetchSocialAccountApplicationsThunk = createAsyncThunk<
    { socialAccountId: string; items: SocialAccountApplicationItem[] },
    { socialAccountId: string; force?: boolean },
    { rejectValue: string }
>(
    'influencer/social-account-applications',
    async ({ socialAccountId, force = false }, { getState, rejectWithValue }) => {
        try {
            const state = getState() as { influencers: influencerSlice };
            const STALE_TIME = apiConfig.FETCH_SOCIAL_ACCOUNT_APPLICATIONS.staleTime;

            if (
                !force &&
                isFresh(
                    state.influencers.social_account_applications?.[socialAccountId]?.last_fetched_at ?? null,
                    STALE_TIME
                )
            ) {
                return rejectWithValue(ERROR.CACHED);
            }

            const data = await fetchSocialAccountApplications(socialAccountId);
            return { socialAccountId, items: normalizeList<SocialAccountApplicationItem>(data) };
        } catch (error) {
            return rejectWithValue(getErrorMessage(error, 'Failed to fetch applications'));
        }
    }
);

export const fetchSocialAccountCollaborationsThunk = createAsyncThunk<
    { socialAccountId: string; items: SocialAccountCollaborationItem[] },
    { socialAccountId: string; force?: boolean },
    { rejectValue: string }
>(
    'influencer/social-account-collaborations',
    async ({ socialAccountId, force = false }, { getState, rejectWithValue }) => {
        try {
            const state = getState() as { influencers: influencerSlice };
            const STALE_TIME = apiConfig.FETCH_SOCIAL_ACCOUNT_COLLABORATIONS.staleTime;

            if (
                !force &&
                isFresh(
                    state.influencers.social_account_collaborations?.[socialAccountId]?.last_fetched_at ?? null,
                    STALE_TIME
                )
            ) {
                return rejectWithValue(ERROR.CACHED);
            }

            const data = await fetchSocialAccountCollaborations(socialAccountId);
            return { socialAccountId, items: normalizeList<SocialAccountCollaborationItem>(data) };
        } catch (error) {
            return rejectWithValue(getErrorMessage(error, 'Failed to fetch collaborations'));
        }
    }
);

export const fetchBasicSocialPostThunk = createAsyncThunk<{post: BasicSocialPost, postId: string}, {force?: boolean, postId: string}, {rejectValue: string}>(
    'influencer/basic-social-post',
    async ({force, postId}, {getState, rejectWithValue}) => {
        try {
            const STALE_TIME = apiConfig.FETCH_BASIC_SOCIAL_POST.staleTime;
            const state = getState() as { influencers: influencerSlice };
            if(!force && isFresh(state.influencers.basic_social_posts?.[postId]?.last_fetched_at ?? null, STALE_TIME)){
                return rejectWithValue(ERROR.CACHED);
            }

            const post = await fetchBasicSocialPost(postId);

            return {post, postId}

        } catch (error) {
            return rejectWithValue(getErrorMessage(error, "Failed to fetch post"));
        }
    }
) 


export const fetchSocialPostThunk = createAsyncThunk<{socialAccountId: string, post: SocialPost, postId: string}, {force?: boolean, socialAccountId: string, postId: string}, {rejectValue: string}>(
    'social-account/social-post',
    async ({force, socialAccountId, postId}, {getState, rejectWithValue}) => {
        try {
            const STALE_TIME = apiConfig.FETCH_SOCIAL_POST.staleTime;
            const state = getState() as { influencers: influencerSlice };
            if(!force && isFresh(state.influencers.social_posts?.[socialAccountId]?.[postId]?.last_fetched_at ?? null, STALE_TIME)){
                return rejectWithValue(ERROR.CACHED);
            }

            const post = await fetchSocialPost(socialAccountId, postId);

            return {post, postId, socialAccountId}

        } catch (error) {
            return rejectWithValue(getErrorMessage(error, "Failed to fetch post"));
        }
    }
) 


export const fetchSocialPostsThunk = createAsyncThunk<
    { socialAccountId: string; posts: { items: SocialPost[]; count: number }; cacheKey: string; page: number },
    {
        socialAccountId: string;
        type: SocialMediaPostType;
        page: number;
        force?: boolean;
        sort?: string;
        limit?: number;
        has_brand_collabs?: 'y';
    },
    { rejectValue: string }
>(
    'social-account/social-posts',
    async ({ force, socialAccountId, type, page, sort = 'like_count', limit, has_brand_collabs }, { getState, rejectWithValue }) => {
        try {
            const state = getState() as { influencers: influencerSlice };
            const STALE_TIME = apiConfig.FETCH_SOCIAL_POSTS.staleTime;
            const cacheKey = getSocialMediaCacheKey(type, { sort, has_brand_collabs });

            if (page === 1 && !force && isFresh(state.influencers.social_media?.[socialAccountId]?.[cacheKey]?.last_fetched_at ?? null, STALE_TIME)) {
                return rejectWithValue(ERROR.CACHED);
            }

            const posts = await fetchSocialPosts(socialAccountId, {
                page,
                sort,
                limit,
                ...(has_brand_collabs ? { has_brand_collabs } : {}),
                ...(type !== 'VIDEO' ? { type } : {}),
            });

            return { posts, socialAccountId, cacheKey, page };
        } catch (error) {
            return rejectWithValue(getErrorMessage(error, "Failed to fetch media!"));
        }
    }
);

export const fetchCollaborationSocialPostsThunk = createAsyncThunk(
    'influencer/social-posts-legacy',
    async (
        data: { socialAccountId: string; mediaProductType: import('types/influencer').MediaProductType; page: number; force?: boolean },
        { getState, rejectWithValue }
    ) => {
        const state = getState() as { influencers: influencerSlice };
        const mediaType = data.mediaProductType;
        const socialAccountId = data.socialAccountId;
        const staleTime = apiConfig.FETCH_SOCIAL_POSTS.staleTime;
        if (
            data.page === 1 &&
            !data.force &&
            isFresh(state.influencers.social_media?.[socialAccountId]?.[mediaType]?.last_fetched_at, staleTime)
        ) {
            return rejectWithValue(ERROR.CACHED);
        }

        try {
            const media = await fetchSocialPosts(socialAccountId, {
                page: data.page,
                sort: 'timestamp_tz',
                ...(mediaType !== 'VIDEO' ? { type: mediaType } : {}),
            });
            return { media, ...data };
        } catch (error) {
            console.log(error);
            return rejectWithValue(ERROR.NOT_FOUND);
        }
    }
);
